/**
 * Design 2026-08-24 (client-version-visibility): "Channel Chat - version is
 * probably where that belongs... 'new feature came in v2 and they're on
 * v1.3' we need to get them updated."
 *
 * `apps/console/src/components/modals/reads.ts` is the wrong home for this —
 * that module is scoped to the five modals (task brief, its own header
 * comment). This is a THIS THREAD read for pane C's Context tab, same shape
 * of seam this pane already has for the AI tab: `./aiOps.ts`'s
 * `createAiOpsClient` (injectable fetch + base URL, defaulting to
 * runtime-config; never throws, resolves an honest "could not answer" state
 * on any non-2xx response or network error instead).
 *
 * Backs `GET /ops/threads/:id/client-presence`
 * (`services/ops/src/routes/thread-reads.ts`), which answers
 * `{ wired: true, live, lastSeen, version }` for a thread whose contact is
 * known and reachable, or `{ wired: false, reason }` for three distinct
 * reasons a dispatcher would act on differently (`not-configured`,
 * `no-contact`, `relay-unreachable`) — carried through here verbatim rather
 * than collapsed into one grey state, same discipline as
 * `../modals/reads.ts`'s `UnwiredReason`.
 */
import { loadRuntimeConfig } from '../../runtime-config'

export interface ClientPresenceRead {
  /** True only once the relay actually answered with a real presence record. */
  wired: boolean
  live?: boolean
  lastSeen?: string | null
  /** The contact's last-reported client app version — absent/null when the relay never heard one. */
  version?: string | null
  /** Why `wired` is false, when the route said so. Absent on a network error or an older ops build. */
  reason?: string
}

export const CLIENT_PRESENCE_SEAM: ClientPresenceRead = { wired: false }

interface ClientPresenceDeps {
  /** Defaults to the global `fetch`. Override in tests. */
  fetchImpl?: typeof fetch
  /** Defaults to reading /runtime-config.json via loadRuntimeConfig(). Pass a literal (or a function
   * returning one) in tests to skip that fetch entirely — mirrors ./aiOps.ts's own deps shape. */
  baseUrl?: string | (() => string | Promise<string>)
}

/**
 * Builds the client-presence read client. `read()` always resolves — never
 * throws — because a route that cannot answer and a genuine network failure
 * both mean the same thing to the Channel row: show the plain channel label,
 * never "unknown".
 */
export function createClientPresenceClient(deps: ClientPresenceDeps = {}) {
  const doFetch: typeof fetch = deps.fetchImpl ?? ((...args) => fetch(...args))

  async function resolveBase(): Promise<string> {
    if (deps.baseUrl !== undefined) {
      return typeof deps.baseUrl === 'function' ? deps.baseUrl() : deps.baseUrl
    }
    return (await loadRuntimeConfig()).apiBase
  }

  async function read(threadId: string): Promise<ClientPresenceRead> {
    try {
      const base = await resolveBase()
      const res = await doFetch(`${base}/ops/threads/${encodeURIComponent(threadId)}/client-presence`, {
        credentials: 'include',
      })
      if (!res.ok) return CLIENT_PRESENCE_SEAM
      const body: unknown = await res.json().catch(() => null)
      if (!body || typeof body !== 'object') return CLIENT_PRESENCE_SEAM
      const b = body as Record<string, unknown>
      if (b.wired !== true) {
        return typeof b.reason === 'string' ? { wired: false, reason: b.reason } : CLIENT_PRESENCE_SEAM
      }
      return {
        wired: true,
        live: b.live === true,
        lastSeen: typeof b.lastSeen === 'string' ? b.lastSeen : null,
        version: typeof b.version === 'string' && b.version.length > 0 ? b.version : null,
      }
    } catch {
      return CLIENT_PRESENCE_SEAM
    }
  }

  return { read }
}

/** The singleton every real caller (ContextPane) uses — same pattern as `aiOps` in ./aiOps.ts. */
export const clientPresence = createClientPresenceClient()
